// ============================================================
// COMPLIANCE TYPES - Geo-blocking, KYC, AMOE, Platform Settings
// ============================================================

// ============================================================
// GEO-BLOCKING TYPES
// ============================================================

export interface GeoCheckResult {
  allowed: boolean;
  country: string;
  countryCode: string;
  region: string;
  regionCode: string;
  city: string;
  isVPN: boolean;
  isProxy: boolean;
  isTor: boolean;
  isHosting: boolean;
  riskScore: number;
  ip: string;
  reason?: GeoBlockReason;
  checkedAt: Date;
}

export type GeoBlockReason = 
  | 'RESTRICTED_STATE'
  | 'RESTRICTED_COUNTRY'
  | 'VPN_DETECTED'
  | 'PROXY_DETECTED'
  | 'TOR_DETECTED'
  | 'HIGH_RISK_SCORE'
  | 'GEO_CHECK_FAILED';

export interface GeoBlockedState {
  code: string;
  name: string;
  reason: string;
}

// Default restricted states for sweepstakes
export const DEFAULT_RESTRICTED_STATES: GeoBlockedState[] = [
  { code: 'WA', name: 'Washington', reason: 'State gambling regulations' },
  { code: 'ID', name: 'Idaho', reason: 'State gambling regulations' },
];

// ============================================================
// KYC / VERIFICATION TYPES
// ============================================================

export type KYCStatus = 'none' | 'pending' | 'verified' | 'rejected' | 'requires_input';

export interface KYCVerification {
  id: string;
  userId: string;
  status: KYCStatus;
  stripeVerificationId?: string;
  diditSessionId?: string;
  verificationSource?: 'stripe_identity' | 'didit';
  verifiedAt?: Date;
  rejectedAt?: Date;
  rejectionReason?: string;
  documentType?: 'id_card' | 'passport' | 'driving_license';
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  addressLine1?: string;
  addressCity?: string;
  addressState?: string;
  addressPostalCode?: string;
  addressCountry?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface VerificationSession {
  sessionId: string;
  clientSecret?: string;
  url: string;
  status: 'requires_input' | 'processing' | 'verified' | 'canceled';
  expiresAt: Date;
}

// ============================================================
// AMOE (Alternative Method of Entry) TYPES
// ============================================================

export interface AMOEClaim {
  id: string;
  userId: string;
  amount: number;
  claimedAt: Date;
  ipAddress: string;
  geoLocation: {
    country: string;
    region: string;
    city: string;
  };
  status: 'granted' | 'rejected';
  rejectionReason?: string;
}

export interface AMOEEligibility {
  eligible: boolean;
  reason?: AMOEIneligibleReason;
  nextEligibleAt?: Date;
  currentPKCash: number;
  lastClaimAt?: Date;
}

export type AMOEIneligibleReason =
  | 'HAS_BALANCE'        // PKCash > 0
  | 'COOLDOWN_ACTIVE'    // Already claimed within 24h
  | 'NOT_VERIFIED'       // Email not verified
  | 'AGE_NOT_VERIFIED'   // Under 18 or age not confirmed
  | 'GEO_RESTRICTED'     // In restricted state
  | 'VPN_DETECTED'       // Using VPN
  | 'ACCOUNT_SUSPENDED'  // Account issues
  | 'DAILY_LIMIT_REACHED'; // Platform daily limit reached

// ============================================================
// REDEMPTION TYPES (Enhanced)
// ============================================================

export type RedemptionStatus = 
  | 'pending_kyc'      // Waiting for KYC verification
  | 'pending_review'   // Waiting for admin review
  | 'approved'         // Admin approved, payout processing
  | 'processing'       // Stripe payout in progress
  | 'completed'        // Payout successful
  | 'rejected'         // Admin rejected
  | 'failed';          // Payout failed

export interface EnhancedRedemption {
  id: string;
  userId: string;
  
  // Request details
  amount: number;
  currency: 'pkcash';
  requestedAt: Date;
  
  // Status tracking
  status: RedemptionStatus;
  statusHistory: RedemptionStatusChange[];
  
  // Compliance checks at time of request
  complianceSnapshot: {
    kycStatus: KYCStatus;
    kycVerifiedAt?: Date;
    geoCheck: GeoCheckResult;
    playthroughComplete: boolean;
    playthroughRequired: number;
    playthroughActual: number;
    accountAge: number; // days
    totalDeposits: number;
    totalRedemptions: number;
    fraudScore: number;
  };
  
  // Admin review
  reviewedBy?: string;
  reviewedAt?: Date;
  reviewNotes?: string;
  
  // Payout details
  stripeConnectAccountId?: string;
  stripePayoutId?: string;
  payoutMethod?: 'bank_account' | 'debit_card';
  paidAt?: Date;
  paidAmount?: number;
  payoutFee?: number;
  
  // Error tracking
  errorMessage?: string;
  errorCode?: string;
  
  // Timestamps
  createdAt: Date;
  updatedAt: Date;
}

export interface RedemptionStatusChange {
  status: RedemptionStatus;
  changedAt: Date;
  changedBy?: string; // 'system' or admin userId
  reason?: string;
}

// ============================================================
// STRIPE CONNECT TYPES
// ============================================================

export interface ConnectAccount {
  id: string;
  userId: string;
  stripeAccountId: string;
  accountStatus: 'pending' | 'active' | 'restricted' | 'disabled';
  payoutsEnabled: boolean;
  chargesEnabled: boolean;
  detailsSubmitted: boolean;
  requirements?: {
    currentlyDue: string[];
    eventuallyDue: string[];
    pastDue: string[];
    pendingVerification: string[];
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface ConnectOnboardingResult {
  success: boolean;
  url?: string;
  error?: string;
}

export interface PayoutResult {
  success: boolean;
  payoutId?: string;
  amount?: number;
  arrivalDate?: Date;
  error?: string;
}

// ============================================================
// PLATFORM SETTINGS TYPES
// ============================================================

export interface PlatformSettings {
  id: 'default';
  
  // Business Info
  businessName: string;
  businessUrl: string;
  supportEmail: string;
  privacyEmail: string;
  
  // Geo-blocking
  restrictedStates: string[];      // ['WA', 'ID']
  restrictedCountries: string[];   // ['XX'] - country codes
  vpnBlockingEnabled: boolean;
  proxyBlockingEnabled: boolean;
  
  // AMOE Settings
  amoeEnabled: boolean;
  amoeAmount: number;              // Default: 1.00

  // Voice: free minutes every player gets per week (Monday reset, PT)
  freeVoiceMinutesPerWeek?: number; // Default: 45
  amoeCooldownHours: number;       // Default: 24
  amoeRequiresZeroBalance: boolean;
  amoeRequiresVerifiedEmail: boolean;
  amoeRequiresAgeVerification: boolean;
  amoeDailyPlatformLimit?: number; // Optional cap on total AMOE grants per day
  
  // Redemption Settings
  minRedemption: number;           // Default: 10
  maxRedemptionDaily: number;      // Default: 500
  maxRedemptionWeekly: number;     // Default: 2000
  maxRedemptionMonthly: number;    // Default: 10000
  playthroughRequirement: number;  // Default: 1 (1x)
  redemptionRequiresKYC: boolean;  // Default: true
  redemptionProcessingDays: string; // Default: "1-3 business days"
  
  // KYC Settings
  kycRequiredForRedemption: boolean;
  kycRequiredForPKCashPlay: boolean;
  kycRequiredForDeposit: boolean;
  kycProvider: 'stripe_identity' | 'didit';
  
  // Age Verification
  minimumAge: number;              // Default: 18
  ageVerificationMethod: 'self_declare' | 'kyc' | 'hybrid';
  
  // Fraud Prevention
  maxAccountsPerIP: number;
  maxAccountsPerDevice: number;
  suspiciousActivityThreshold: number;
  autoFreezeHighRiskRedemptions: boolean;
  
  // Timestamps
  updatedAt: Date;
  updatedBy: string;
}

// Default platform settings
export const DEFAULT_PLATFORM_SETTINGS: Omit<PlatformSettings, 'updatedAt' | 'updatedBy'> = {
  id: 'default',
  
  // Business Info
  businessName: 'PokerSweepStars',
  businessUrl: 'https://pokersweepstars.web.app',
  supportEmail: 'support@pokersweepstars.com',
  privacyEmail: 'privacy@pokersweepstars.com',
  
  // Geo-blocking
  restrictedStates: ['WA', 'ID'],
  restrictedCountries: [],
  vpnBlockingEnabled: true,
  proxyBlockingEnabled: true,
  
  // AMOE Settings
  amoeEnabled: true,
  amoeAmount: 1.00,
  freeVoiceMinutesPerWeek: 45,
  amoeCooldownHours: 24,
  amoeRequiresZeroBalance: true,
  amoeRequiresVerifiedEmail: true,
  amoeRequiresAgeVerification: true,
  // NOTE: never give an optional field an explicit `undefined` default.
  // Firestore rejects undefined values, so spreading these defaults into
  // setDoc threw and the ENTIRE settings save failed - including the geo
  // restricted-states list. The key is simply omitted when unset.

  // Redemption Settings
  minRedemption: 10,
  maxRedemptionDaily: 500,
  maxRedemptionWeekly: 2000,
  maxRedemptionMonthly: 10000,
  playthroughRequirement: 1,
  redemptionRequiresKYC: true,
  redemptionProcessingDays: '1-3 business days',
  
  // KYC Settings
  kycRequiredForRedemption: true,
  kycRequiredForPKCashPlay: false,
  kycRequiredForDeposit: false,
  kycProvider: 'didit',
  
  // Age Verification
  minimumAge: 18,
  ageVerificationMethod: 'hybrid',
  
  // Fraud Prevention
  maxAccountsPerIP: 3,
  maxAccountsPerDevice: 2,
  suspiciousActivityThreshold: 70,
  autoFreezeHighRiskRedemptions: true,
};

// ============================================================
// AUDIT LOG TYPES
// ============================================================

export type AuditAction =
  | 'AMOE_CLAIM'
  | 'AMOE_REJECTED'
  | 'KYC_STARTED'
  | 'KYC_VERIFIED'
  | 'KYC_REJECTED'
  | 'GEO_CHECK'
  | 'GEO_BLOCKED'
  | 'REDEMPTION_REQUESTED'
  | 'REDEMPTION_APPROVED'
  | 'REDEMPTION_REJECTED'
  | 'REDEMPTION_PAID'
  | 'REDEMPTION_FAILED'
  | 'SETTINGS_UPDATED'
  | 'ADMIN_ACTION';

export interface AuditLog {
  id: string;
  action: AuditAction;
  userId: string;
  adminId?: string;
  details: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  timestamp: Date;
}

// ============================================================
// API RESPONSE TYPES
// ============================================================

export interface ComplianceCheckResult {
  allowed: boolean;
  checks: {
    geoCheck: { passed: boolean; reason?: string };
    ageCheck: { passed: boolean; reason?: string };
    kycCheck: { passed: boolean; reason?: string };
    vpnCheck: { passed: boolean; reason?: string };
  };
  overallReason?: string;
}

export interface RedemptionEligibility {
  eligible: boolean;
  reason?: string;
  checks: {
    minAmount: { passed: boolean; required: number; actual: number };
    maxDaily: { passed: boolean; limit: number; used: number };
    maxWeekly: { passed: boolean; limit: number; used: number };
    playthrough: { passed: boolean; required: number; actual: number };
    kycStatus: { passed: boolean; status: KYCStatus };
    geoLocation: { passed: boolean; location?: string };
    accountAge: { passed: boolean; days: number };
  };
}

// ============================================================
// STATE NAME MAPPING
// ============================================================

export const US_STATES: Record<string, string> = {
  'AL': 'Alabama',
  'AK': 'Alaska',
  'AZ': 'Arizona',
  'AR': 'Arkansas',
  'CA': 'California',
  'CO': 'Colorado',
  'CT': 'Connecticut',
  'DE': 'Delaware',
  'FL': 'Florida',
  'GA': 'Georgia',
  'HI': 'Hawaii',
  'ID': 'Idaho',
  'IL': 'Illinois',
  'IN': 'Indiana',
  'IA': 'Iowa',
  'KS': 'Kansas',
  'KY': 'Kentucky',
  'LA': 'Louisiana',
  'ME': 'Maine',
  'MD': 'Maryland',
  'MA': 'Massachusetts',
  'MI': 'Michigan',
  'MN': 'Minnesota',
  'MS': 'Mississippi',
  'MO': 'Missouri',
  'MT': 'Montana',
  'NE': 'Nebraska',
  'NV': 'Nevada',
  'NH': 'New Hampshire',
  'NJ': 'New Jersey',
  'NM': 'New Mexico',
  'NY': 'New York',
  'NC': 'North Carolina',
  'ND': 'North Dakota',
  'OH': 'Ohio',
  'OK': 'Oklahoma',
  'OR': 'Oregon',
  'PA': 'Pennsylvania',
  'RI': 'Rhode Island',
  'SC': 'South Carolina',
  'SD': 'South Dakota',
  'TN': 'Tennessee',
  'TX': 'Texas',
  'UT': 'Utah',
  'VT': 'Vermont',
  'VA': 'Virginia',
  'WA': 'Washington',
  'WV': 'West Virginia',
  'WI': 'Wisconsin',
  'WY': 'Wyoming',
  'DC': 'District of Columbia',
};